'use client';

import { FC, useState } from 'react';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { useForm } from 'react-hook-form';
import cookies from 'js-cookie';
import { registerContact } from '@/actions/hubspot/registerContact';
import { emailRegex, isCorporateEmail } from '@/utils';

const DEFAULT_HUBSPOT_PORTAL_ID = '7119515';
const DEFAULT_HUBSPOT_FORM_ID = 'c54880e9-1a1d-4609-a81e-a1133f6b5d1c';

interface INewsletterForm {
  email: string;
}

enum FormStatus {
  DIRTY = 'DIRTY',
  SUBMITTED = 'SUBMITTED',
  FAILED = 'FAILED',
}

type SocialIconItem = {
  icon?: import('@prismicio/client').ImageField;
  link?: import('@prismicio/client').LinkField;
};

type LinkItem = {
  link?: import('@prismicio/client').LinkField;
};

type FooterCustomPrimary = {
  logo?: import('@prismicio/client').ImageField;
  social_icons?: SocialIconItem[];
  newsletter_heading?: string;
  newsletter_placeholder?: string;
  newsletter_button_label?: string;
  newsletter_form_id?: string;
  privacy_notice?: import('@prismicio/client').RichTextField;
  col1_title?: string;
  col1_links?: LinkItem[];
  col2_title?: string;
  col2_links?: LinkItem[];
  copyright_text?: string;
  bottom_links?: LinkItem[];
};

type FooterCustomSlice = {
  slice_type: string;
  variation: string;
  primary: FooterCustomPrimary;
};

const FooterCustom: FC<{ slice: FooterCustomSlice }> = ({ slice }) => {
  const {
    logo,
    social_icons,
    newsletter_heading,
    newsletter_placeholder,
    newsletter_button_label,
    newsletter_form_id,
    privacy_notice,
    col1_title,
    col1_links,
    col2_title,
    col2_links,
    copyright_text,
    bottom_links,
  } = slice.primary;

  const hubspotFormId = newsletter_form_id || DEFAULT_HUBSPOT_FORM_ID;

  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors: { email: emailError },
      isSubmitting,
    },
  } = useForm<INewsletterForm>({ mode: 'onBlur' });

  const [status, setStatus] = useState<FormStatus>(FormStatus.DIRTY);

  const formSubmit = async (values: INewsletterForm) => {
    reset();
    try {
      const { postSubmitAction } = await registerContact({
        properties: { email: values.email },
        hubspotPortalIdForm: DEFAULT_HUBSPOT_PORTAL_ID,
        hubspotGuidForm: hubspotFormId,
        hubspotutkCookie: cookies.get('hubspotutk'),
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName:
          typeof document !== 'undefined'
            ? document.title
            : 'Footer Newsletter',
      });
      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.assign(postSubmitAction.value);
        return;
      }
      setStatus(FormStatus.SUBMITTED);
    } catch {
      setStatus(FormStatus.FAILED);
    }
  };

  return (
    <footer
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full bg-white font-inter"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 py-14">
        <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-20">
          {/* LEFT: Logo + Newsletter */}
          <div className="flex-1 max-w-[440px] flex flex-col gap-6">
            {/* Logo */}
            {isFilled.image(logo) && (
              <PrismicNextImage
                field={logo}
                className="h-8 w-auto"
                width={120}
                height={32}
                fallbackAlt=""
              />
            )}

            {/* Newsletter heading */}
            {newsletter_heading && (
              <p className="text-[#0E1110] text-[15px] leading-[150%]">
                {newsletter_heading}
              </p>
            )}

            {/* Newsletter form */}
            {status === FormStatus.SUBMITTED ? (
              <div className="text-[#05F273] font-medium text-[15px] py-2">
                Thank you for subscribing!
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(formSubmit)}
                className="flex items-end gap-3"
              >
                <div
                  className={`flex-1 border-b ${
                    emailError ? 'border-red-400' : 'border-[#999]'
                  } flex items-center gap-1 pb-2`}
                >
                  <input
                    type="email"
                    placeholder={newsletter_placeholder || 'Enter your email'}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: emailRegex,
                        message: 'Please enter a valid email',
                      },
                      validate: {
                        isCorporateEmail: (v) =>
                          isCorporateEmail(v) || 'Please use a corporate email',
                      },
                    })}
                    className="flex-1 bg-transparent text-[#0E1110] placeholder:text-[#999] text-[14px] focus:outline-none"
                  />
                  <span className="text-red-500 text-sm shrink-0">*</span>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="primary-button shrink-0 !px-5 !py-2.5 !text-[14px] disabled:opacity-50"
                >
                  {isSubmitting
                    ? 'Subscribing...'
                    : newsletter_button_label || 'Suscribe'}
                </button>
              </form>
            )}
            {emailError && (
              <p className="text-red-400 text-[12px] -mt-3">
                {emailError.message}
              </p>
            )}
            {status === FormStatus.FAILED && (
              <p className="text-red-400 text-[12px] -mt-3">
                Something went wrong. Please try again.
              </p>
            )}

            {/* Privacy notice */}
            {privacy_notice && (
              <div className="text-[#666] text-[12px] leading-[160%] [&_a]:underline [&_a:hover]:text-[#05F273]">
                <PrismicRichText
                  field={privacy_notice}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="mb-0">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* MIDDLE: Social icons */}
          {social_icons && social_icons.length > 0 && (
            <div className="flex items-center gap-5 lg:pt-1">
              {social_icons.map((item, i) =>
                isFilled.image(item.icon) ? (
                  <PrismicNextLink
                    key={i}
                    field={item.link}
                    className="text-[#0E1110] hover:opacity-70 transition-opacity"
                  >
                    <PrismicNextImage
                      field={item.icon}
                      className="w-5 h-5 object-contain"
                      width={20}
                      height={20}
                      fallbackAlt=""
                    />
                  </PrismicNextLink>
                ) : null,
              )}
            </div>
          )}

          {/* RIGHT: Nav columns */}
          <div className="flex gap-12 lg:gap-16 lg:pt-1">
            {(col1_title || (col1_links && col1_links.length > 0)) && (
              <nav className="flex flex-col gap-4 min-w-[120px]">
                {col1_title && (
                  <h3 className="font-semibold text-[#0E1110] text-[15px] leading-[150%]">
                    {col1_title}
                  </h3>
                )}
                <ul className="flex flex-col gap-3">
                  {col1_links?.map((item, i) => (
                    <li key={i}>
                      <PrismicNextLink
                        field={item.link}
                        className="text-[#555] text-[14px] hover:text-[#05F273] transition-colors"
                      />
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            {(col2_title || (col2_links && col2_links.length > 0)) && (
              <nav className="flex flex-col gap-4 min-w-[120px]">
                {col2_title && (
                  <h3 className="font-semibold text-[#0E1110] text-[15px] leading-[150%]">
                    {col2_title}
                  </h3>
                )}
                <ul className="flex flex-col gap-3">
                  {col2_links?.map((item, i) => (
                    <li key={i}>
                      <PrismicNextLink
                        field={item.link}
                        className="text-[#555] text-[14px] hover:text-[#05F273] transition-colors"
                      />
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </div>

        {/* Divider */}
        <hr className="my-8 border-[#E5E5E5]" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {copyright_text && (
            <p className="text-[#666] text-[13px]">{copyright_text}</p>
          )}
          {bottom_links && bottom_links.length > 0 && (
            <div className="flex flex-wrap gap-6 items-center">
              {bottom_links.map((item, i) => (
                <PrismicNextLink
                  key={i}
                  field={item.link}
                  className="text-[#666] text-[13px] underline hover:text-[#05F273] transition-colors"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default FooterCustom;
